import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCw, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";

const RATING_DEFS = [
    { key: "again", color: "hsl(0,72%,55%)" },
    { key: "hard", color: "hsl(38,92%,58%)" },
    { key: "good", color: "hsl(206,70%,52%)" },
    { key: "easy", color: "hsl(152,52%,46%)" },
];
// Ratings map to backend keys (again/hard/medium/easy).
const RATING_TO_API = { again: "again", hard: "hard", good: "medium", easy: "easy" };

export const FlashcardReview = ({ cards = [], onRate, onDone, showTopic = true }) => {
    const { t } = useTranslation();
    const [i, setI] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [busy, setBusy] = useState(false);

    const card = cards[i];

    const rate = useCallback(async (ratingKey) => {
        if (busy || !card) return;
        setBusy(true);
        try { await onRate?.(card, RATING_TO_API[ratingKey] || ratingKey); } catch (e) {}
        setBusy(false);
        if (i + 1 >= cards.length) { onDone?.(); }
        else { setI(i + 1); setFlipped(false); }
    }, [busy, card, i, cards.length, onRate, onDone]);

    useEffect(() => {
        const onKey = (e) => {
            if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
            if (e.code === "Space") { e.preventDefault(); setFlipped((f) => !f); }
            else if (flipped && ["1", "2", "3", "4"].includes(e.key)) rate(RATING_DEFS[parseInt(e.key) - 1].key);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [flipped, rate]);

    if (!card) return null;
    const pct = (i / cards.length) * 100;

    return (
        <div className="flex flex-col items-center" data-testid="flashcard-review">
            <div className="mb-4 flex w-full items-center justify-between">
                <span className="mono-label text-t4">{t("fcReview.card", { i: i + 1, n: cards.length })}</span>
                {showTopic && card.topic && <span className="rounded-full bg-surface-2 px-2.5 py-1 text-xs text-t3">{card.topic}</span>}
            </div>
            <div className="mb-4 h-1 w-full overflow-hidden rounded-full bg-surface-3">
                <div className="h-full rounded-full bg-[hsl(var(--primary))] transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>

            <button
                data-testid="flashcard-flip"
                onClick={() => setFlipped((f) => !f)}
                className="relative flex min-h-[260px] w-full items-center justify-center rounded-3xl border border-border bg-surface-1 p-8 text-center transition-colors hover:bg-surface-2"
            >
                <AnimatePresence mode="wait">
                    <motion.div
                        key={flipped ? "back" : "front"}
                        initial={{ rotateX: 90, opacity: 0 }}
                        animate={{ rotateX: 0, opacity: 1 }}
                        exit={{ rotateX: -90, opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="mono-label mb-3 text-t4">{flipped ? t("fcReview.answer") : t("fcReview.question")}</div>
                        <p className={cn("font-reading leading-relaxed", flipped ? "text-[18px] text-t1" : "text-[20px] text-t1")}>
                            {flipped ? card.back : card.front}
                        </p>
                        {!flipped && card.hint && <p className="mt-3 text-xs text-t4">{t("fcReview.hint")}: {card.hint}</p>}
                        {card.important && <Star className="absolute right-4 top-4 h-4 w-4 fill-[hsl(var(--warning))] text-[hsl(var(--warning))]" />}
                    </motion.div>
                </AnimatePresence>
            </button>

            <div className="mt-5 w-full">
                {!flipped ? (
                    <button data-testid="flashcard-reveal" onClick={() => setFlipped(true)} className="flex w-full items-center justify-center gap-2 rounded-xl bg-surface-3 py-3 text-sm font-medium text-t1 transition-colors hover:bg-surface-2">
                        <RotateCw className="h-4 w-4" /> {t("fcReview.reveal")} <span className="mono-label text-t4">{t("fcReview.space")}</span>
                    </button>
                ) : (
                    <div className="grid grid-cols-4 gap-2">
                        {RATING_DEFS.map((r, idx) => (
                            <button
                                key={r.key}
                                data-testid={`rate-${r.key}`}
                                disabled={busy}
                                onClick={() => rate(r.key)}
                                className="flex flex-col items-center gap-0.5 rounded-xl border border-border py-2.5 text-sm font-medium transition-transform duration-150 hover:-translate-y-0.5"
                                style={{ color: r.color }}
                            >
                                {t(`fcReview.${r.key}`)}
                                <span className="mono-label text-[9px] text-t4">{idx + 1}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
